/**
 * Shared number formatters — the one source of truth for money/percent text.
 *
 * Percent semantics differ by data source: Shopify analytics deliver fractions
 * (0.123), Power BI pacing delivers percentages (12.3). Pick the right one.
 */

/** Full currency, signed: -$1,234 / $1,234 */
export function formatCurrency(value: number | null | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) return "$0";
  const formatted = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return value < 0 ? `-$${formatted}` : `$${formatted}`;
}

/** Compact currency: $1.2M / $845.2K / $312 (signed) */
export function formatCurrencyCompact(value: number): string {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

/** Chart-axis currency: $1.2M / $142K / $85 (K rounded to whole number) */
export function formatCurrencyAxis(value: number): string {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs}`;
}

/** Signed percent from a percentage number: +4.2% / -1.3% */
export function formatPctSigned(value: number): string {
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

/** Plain percent from a percentage number: 4.2% */
export function formatPct(value: number): string {
  return `${value.toFixed(1)}%`;
}

/** Percent from a fraction: 0.123 → 12.3% (Shopify conversion rates etc.) */
export function formatPctFromFraction(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

/** Thousands-separated integer: 12,345 */
export function formatNumber(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
}
